import { useDraggable, useDroppable } from '@dnd-kit/core';

import { equationLines } from '../core/types';

import { VifAnswerTile } from './VifAnswerTile';
import './VifGridCell.scss';

export interface VifGridCellProps {
  id: string;
  index: number;
  /** Top half: the equation. Fixed for the life of the board. */
  equation: string;
  /** Bottom half: the answer currently sitting here. */
  answer: string;
  locked: boolean;
  rejecting: boolean;
  dragging: boolean;
  /** Board-wide input block (mid-rejection, or board finished). */
  disabled: boolean;
  /**
   * Whether the problem text is showing. Before START the canvas game blanks
   * both halves of every tile (`resetAllTiles` writes `''` into `text3` and
   * `t.textb`), so the grid is visible but reveals nothing.
   */
  revealed: boolean;
  preString?: string;
  large?: boolean;
  /** Tapping this tile once it is solved and revealed. Auditory Memory boards only. */
  onSpeak?: (index: number) => void;
}

/**
 * One board tile. The whole tile is both the drag handle and the drop target -
 * picking it up anywhere carries its answer, which is the value a drop trades
 * (legacy `tradePiece` swaps `tileOrigAnswers`, never the equations).
 */
export function VifGridCell({
  id,
  index,
  equation,
  answer,
  locked,
  rejecting,
  dragging,
  disabled,
  revealed,
  preString = '',
  large = false,
  onSpeak,
}: VifGridCellProps) {
  const interactive = revealed && !locked && !disabled;
  const speakable = !!onSpeak && revealed && locked && !disabled;

  // Legacy `nextProblem` sets `jiggleEnabled` on every dealt tile and
  // `correctAnswer`/`checkForBonus` clear it once a tile is solved, so only
  // unsolved answers wobble.
  const jiggling = revealed && !locked && !dragging;

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id,
    disabled: !interactive,
  });

  const {
    setNodeRef: setDragRef,
    attributes,
    listeners,
  } = useDraggable({ id, disabled: !interactive });

  const classes = ['vif-grid-cell'];
  // Gated on `revealed`, like `jiggling` below: an abandoned round's solved
  // tiles stay locked in board state (only a fresh deal on the next START
  // clears them), so without this a concealed board could show a stray
  // locked cell left over from the round the player was previously on.
  if (locked && revealed) {
    classes.push('vif-grid-cell--locked');
  }
  // Gated for the same reason. The block board returns to START after a
  // wrong answer without redealing, so the rejected cell stays marked in
  // board state until START - and the rejecting style fades it to nothing,
  // leaving a hole in the concealed board.
  if (rejecting && revealed) {
    classes.push('vif-grid-cell--rejecting');
  }
  if (isOver && !dragging) {
    classes.push('vif-grid-cell--over');
  }
  if (dragging) {
    classes.push('vif-grid-cell--dragging');
  }
  if (jiggling) {
    classes.push('vif-grid-cell--jiggling');
  }
  if (speakable) {
    classes.push('vif-grid-cell--speakable');
  }

  return (
    <div
      ref={setDropRef}
      className={classes.join(' ')}
      data-index={index}
      data-testid={`vif-cell-${index}`}
      data-locked={locked || undefined}
      data-revealed={revealed || undefined}
      aria-label={
        revealed
          ? `${equation} currently answered ${preString}${answer}`
          : undefined
      }
      // Staggers the wobble so the tiles do not move in lockstep, which
      // stands in for the per-tile random rotation the canvas game picks.
      style={{ '--vif-jiggle-index': index } as React.CSSProperties}
      onClick={() => {
        if (speakable) {
          onSpeak(index);
        }
      }}
      {...attributes}
      {...listeners}
    >
      <span className="vif-grid-cell__equation">
        {revealed
          ? equationLines(equation).map((line, lineIndex) => (
              <span className="vif-grid-cell__equation-line" key={lineIndex}>
                {line}
              </span>
            ))
          : ''}
      </span>

      {/*
       * `setDragRef` (not the whole cell) so `DragOverlay` measures the
       * answer plate's own rect. dnd-kit sizes and positions the overlay from
       * whatever node the drag ref points to; pointing it at the full tile
       * left the overlay anchored to the tile's top-left corner - above the
       * equation - creating a visible gap between the cursor and the number.
       */}
      <span
        ref={setDragRef}
        className="vif-grid-cell__answer"
        aria-hidden="true"
      >
        <VifAnswerTile
          answer={revealed ? answer : ''}
          preString={revealed ? preString : ''}
          large={large}
          locked={locked}
        />
      </span>
    </div>
  );
}
